import { useEffect, useRef } from "react";

const TABBABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

const isTabbable = (element: HTMLElement) =>
  element.tabIndex >= 0 &&
  !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
  element.getClientRects().length > 0;

const follows = (reference: Node, other: Node) =>
  Boolean(reference.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING);

export function getTabbables(containers: Element[]): HTMLElement[] {
  return containers
    .flatMap((container) => Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)))
    .filter(isTabbable)
    .sort((a, b) => (follows(a, b) ? -1 : 1));
}

/**
 * Where Tab should go instead of the browser default, or null to let the
 * browser move focus. Only the ends of the sequence wrap, so the order inside
 * the containers stays the DOM order.
 */
export function getWrapTarget(
  active: Element | null,
  tabbables: HTMLElement[],
  containers: Element[],
  backward: boolean
): HTMLElement | null {
  if (tabbables.length === 0) {
    return null;
  }
  const first = tabbables[0];
  const last = tabbables[tabbables.length - 1];

  // When a focused control becomes disabled, focus falls to <body> but the
  // browser still continues Tab from that control.
  if (!active || !containers.some((container) => container.contains(active))) {
    return null;
  }
  // A dialog opened on top of this one manages its own Tab order.
  const dialog = active.closest(DIALOG_SELECTOR);
  if (dialog && !containers.includes(dialog)) {
    return null;
  }

  if (backward) {
    return tabbables.some((element) => follows(element, active)) ? null : last;
  }
  return tabbables.some((element) => follows(active, element)) ? null : first;
}

/**
 * Keeps Tab and Shift+Tab inside a dialog that isn't portaled, plus any
 * sibling panels that belong to it (e.g. a sidecar rendered next to it).
 */
export default function useTabWrap(
  isActive: boolean,
  getContainers: () => Array<Element | null | undefined>
) {
  const getContainersRef = useRef(getContainers);
  useEffect(() => {
    getContainersRef.current = getContainers;
  });

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Editors that use Tab themselves (e.g. dot-phrase completion) prevent default.
      if (
        event.key !== "Tab" ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }
      const containers = getContainersRef
        .current()
        .filter((container): container is Element => Boolean(container));
      if (containers.length === 0) {
        return;
      }
      const target = getWrapTarget(
        document.activeElement,
        getTabbables(containers),
        containers,
        event.shiftKey
      );
      if (target) {
        event.preventDefault();
        target.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isActive]);
}
